import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Mail,
  Bot,
  CreditCard,
  Printer,
  Zap,
  ArrowRight,
  MessageSquare,
  DollarSign,
  Shield,
  Package,
} from 'lucide-react'

const verticals = [
  { icon: Mail, name: 'SnailBot', label: 'Letters', description: 'Print and post physical letters from AI. Available now.', active: true },
  { icon: Package, name: 'WineBot', label: 'Wine', description: 'Custom-labelled wine bottles with AI-designed labels. Available now.', active: true },
  { icon: Package, name: 'FlowerBot', label: 'Flowers', description: 'Fresh flower arrangements delivered to any door.', active: false },
  { icon: Package, name: 'GiftBot', label: 'Gifts', description: 'Curated gift hampers, cakes, and more.', active: false },
]

const steps = [
  { icon: MessageSquare, title: 'Tell AI what to send', description: 'Describe your letter in plain language. AI drafts it for you, or supply your own HTML.' },
  { icon: DollarSign, title: 'Pay per order or use credits', description: 'No account needed for your first send. Pay $4.50 per letter, or top up credits for volume.' },
  { icon: Printer, title: 'We print and post it', description: 'Printed on A4, folded, enveloped, and posted via Australia Post within 1 business day.' },
]

const features = [
  { icon: Bot, title: 'MCP Server', description: 'Connect Claude, ChatGPT, or any MCP-compatible AI. Send letters from conversation.' },
  { icon: Zap, title: 'REST API', description: 'Full API with bearer token auth. Automate physical mail from any app or workflow.' },
  { icon: CreditCard, title: 'Flexible Payments', description: 'Pay per order via Stripe, or pre-fund credits for instant processing. $20/$50/$100/$200 top-ups.' },
  { icon: Shield, title: 'Scoped API Tokens', description: 'Granular permissions: orders:write, account:read, admin:write. Full control over access.' },
]

export function LandingPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />

        <div className="container relative mx-auto max-w-5xl px-4 py-24 md:py-32">
          <div className="flex flex-col items-center text-center">
            <Badge variant="secondary" className="mb-4">
              AI-Native Physical Commerce
            </Badge>

            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl max-w-4xl">
              Send real mail{' '}
              <span className="text-primary">from AI</span>
            </h1>

            <p className="mt-6 text-lg text-muted-foreground max-w-2xl md:text-xl">
              One API to send physical letters, wine, flowers, and gifts.
              Tell your AI what to send. We print it, pack it, post it.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild>
                <Link to="/sign-up">
                  Get Started Free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/sign-in">
                  Sign In
                </Link>
              </Button>
            </div>

            <p className="mt-4 text-sm text-muted-foreground">
              Letters from $3.50. No monthly fees. Pay per order.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Three steps to real mail
            </h2>
            <p className="mt-4 text-muted-foreground">
              From AI conversation to letterbox in under 3 days.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {steps.map((step, i) => (
              <div key={step.title} className="flex flex-col items-center text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold mb-4">
                  {i + 1}
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MCP Demo */}
      <section className="py-24">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="grid gap-12 md:grid-cols-2 items-center">
            <div>
              <Badge variant="secondary" className="mb-4">MCP Server</Badge>
              <h2 className="text-3xl font-bold tracking-tight mb-4">
                Send mail from Claude
              </h2>
              <p className="text-muted-foreground mb-6">
                Add SendBot as an MCP server in Claude Desktop, Cursor, or any MCP-compatible tool.
                Then just ask it to send a letter.
              </p>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5 shrink-0">
                    <Mail className="h-3 w-3 text-primary" />
                  </div>
                  <span><strong>send_letter</strong> — Send a physical letter with full address details</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5 shrink-0">
                    <Bot className="h-3 w-3 text-primary" />
                  </div>
                  <span><strong>draft_letter</strong> — AI writes the letter, then prints and posts it</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5 shrink-0">
                    <DollarSign className="h-3 w-3 text-primary" />
                  </div>
                  <span><strong>estimate_cost</strong> — Check pricing before committing</span>
                </div>
              </div>
            </div>

            <Card className="bg-muted/50 border-border/50">
              <CardContent className="pt-6">
                <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap text-muted-foreground">
{`{
  "mcpServers": {
    "sendbot": {
      "url": "https://sendbot.au/mcp",
      "headers": {
        "Authorization": "Bearer sb_your_token"
      }
    }
  }
}`}
                </pre>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Built for developers and AI
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {features.map((feature) => (
              <Card key={feature.title} className="border border-border/50">
                <CardContent className="pt-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-3">
                    <feature.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold mb-1">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Verticals */}
      <section className="py-24">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              One platform, many verticals
            </h2>
            <p className="mt-4 text-muted-foreground">
              Letters ship first. More verticals launching soon.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {verticals.map((v) => (
              <Card key={v.name} className={`border ${v.active ? 'border-primary/50 bg-primary/5' : 'border-border/50 opacity-60'}`}>
                <CardContent className="pt-6 text-center">
                  <v.icon className={`h-8 w-8 mx-auto mb-3 ${v.active ? 'text-primary' : 'text-muted-foreground'}`} />
                  <h3 className="font-semibold">{v.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{v.label}</p>
                  <p className="text-xs text-muted-foreground mt-2">{v.description}</p>
                  {v.active && (
                    <Badge className="mt-3" variant="default">Live</Badge>
                  )}
                  {!v.active && (
                    <Badge className="mt-3" variant="secondary">Coming Soon</Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
            Simple pricing
          </h2>
          <p className="text-muted-foreground mb-10">
            No subscriptions. No monthly fees. Pay for what you send.
          </p>

          <Card className="border-primary/50 max-w-md mx-auto">
            <CardContent className="pt-6">
              <h3 className="text-2xl font-bold mb-1">Letters from $3.50</h3>
              <p className="text-muted-foreground text-sm mb-6">Per letter, includes printing + postage</p>
              <div className="space-y-2 text-sm text-left">
                <div className="flex justify-between"><span>Base (B&W, standard post)</span><span className="font-mono">$3.50</span></div>
                <div className="flex justify-between"><span>Colour printing</span><span className="font-mono">+$1.00</span></div>
                <div className="flex justify-between"><span>Priority post</span><span className="font-mono">+$2.00</span></div>
                <div className="flex justify-between"><span>AI-drafted content</span><span className="font-mono">+$1.00</span></div>
                <div className="flex justify-between"><span>Registered post</span><span className="font-mono">+$5.50</span></div>
                <div className="flex justify-between"><span>Extra pages</span><span className="font-mono">+$0.30/page</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
            Send your first letter
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Sign up, get an API token, and send a real letter in under 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/sign-up">
                Create Account
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
